import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { usersService } from '../services';

const STORAGE_KEY_VIEW_MODE = 'h2okay_view_mode';

export type ViewMode = 'consultant' | 'facility_manager';

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  effectiveFacilityManagerId: string | null;
  effectiveClientId: string | null;
  isFacilityView: boolean;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

interface ViewModeProviderProps {
  children: ReactNode;
}

export const ViewModeProvider: React.FC<ViewModeProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_VIEW_MODE);
      if (stored === 'facility_manager' || stored === 'consultant') return stored;
    } catch {
      /* ignore */
    }
    return 'consultant';
  });
  const [effectiveFacilityManagerId, setEffectiveFacilityManagerId] = useState<string | null>(null);
  const [effectiveClientId, setEffectiveClientId] = useState<string | null>(null);

  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY_VIEW_MODE, mode);
    } catch {
      /* ignore */
    }
  }, []);

  // Clear a stale facility_manager view-mode left in localStorage when the
  // current user is a consultant — otherwise Dashboard.tsx redirects them to
  // /facilities on every load.
  useEffect(() => {
    if (currentUser?.role === 'consultant' && viewMode === 'facility_manager') {
      setViewMode('consultant');
    }
  }, [currentUser?.role, viewMode, setViewMode]);

  useEffect(() => {
    if (viewMode !== 'facility_manager') {
      setEffectiveFacilityManagerId(null);
      setEffectiveClientId(null);
      return;
    }
    if (currentUser?.role === 'facility_manager') {
      setEffectiveFacilityManagerId(currentUser.id);
      setEffectiveClientId(currentUser.client_id ?? null);
      return;
    }
    let cancelled = false;
    const resolve = async () => {
      try {
        const users = await usersService.getUsers();
        const fm = users.find(u => u.role === 'facility_manager');
        if (!cancelled) {
          setEffectiveFacilityManagerId(fm?.id ?? null);
          setEffectiveClientId(fm?.client_id ?? null);
        }
      } catch {
        if (!cancelled) {
          setEffectiveFacilityManagerId(null);
          setEffectiveClientId(null);
        }
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [viewMode, currentUser?.id, currentUser?.role, currentUser?.client_id]);

  const isFacilityView = viewMode === 'facility_manager';

  return (
    <ViewModeContext.Provider value={{ viewMode, setViewMode, effectiveFacilityManagerId, effectiveClientId, isFacilityView }}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = (): ViewModeContextType => {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};

export default ViewModeContext;
